import { isArray, isBoolean, isNull, isNullish, isUndefined } from "../utils/index.mjs";
import { CIRCULAR_VALUE, FUNCTION_VALUE, MAX_JSON_SAFE_VALUE_DEPTH, MAX_JSON_SAFE_VALUE_ITEMS, MAX_JSON_SAFE_VALUE_NODES, TRUNCATED_VALUE, UNSERIALIZABLE_VALUE, sanitizeString } from "../utils/json-utils.mjs";
const OTLP_SEVERITY_MAP = {
    trace: {
        text: 'TRACE',
        number: 1
    },
    debug: {
        text: 'DEBUG',
        number: 5
    },
    info: {
        text: 'INFO',
        number: 9
    },
    warn: {
        text: 'WARN',
        number: 13
    },
    error: {
        text: 'ERROR',
        number: 17
    },
    fatal: {
        text: 'FATAL',
        number: 21
    }
};
const DEFAULT_OTLP_SEVERITY = OTLP_SEVERITY_MAP.info;
function getOtlpSeverityText(level) {
    return (OTLP_SEVERITY_MAP[level] || DEFAULT_OTLP_SEVERITY).text;
}
function getOtlpSeverityNumber(level) {
    return (OTLP_SEVERITY_MAP[level] || DEFAULT_OTLP_SEVERITY).number;
}
const INT64_RANGE_LIMIT = 9223372036854775808;
const propertyIsEnumerable = Object.prototype.propertyIsEnumerable;
function newState() {
    return {
        ancestors: new WeakSet(),
        remainingNodes: MAX_JSON_SAFE_VALUE_NODES
    };
}
function toOtlpAnyValue(value, logger) {
    try {
        return encodeAnyValue(value, logger, newState(), 0);
    } catch  {
        return {
            stringValue: UNSERIALIZABLE_VALUE
        };
    }
}
function toOtlpKeyValueList(attrs, logger) {
    try {
        return encodeKeyValueList(attrs, logger, newState(), 0);
    } catch  {
        return [];
    }
}
function encodeAnyValue(value, logger, state, depth) {
    if (state.remainingNodes <= 0) return {
        stringValue: TRUNCATED_VALUE
    };
    state.remainingNodes--;
    if (isBoolean(value)) return {
        boolValue: value
    };
    if ('number' == typeof value) {
        if (!Number.isFinite(value)) return {
            stringValue: String(value)
        };
        if (Number.isInteger(value)) {
            if (Number.isSafeInteger(value)) return {
                intValue: String(value)
            };
            if ('undefined' == typeof BigInt) return {
                stringValue: String(value)
            };
            const decimal = BigInt(value).toString();
            if (value >= INT64_RANGE_LIMIT || value < -INT64_RANGE_LIMIT) {
                logger?.debug(`Attribute ${decimal} is outside the int64 range; encoding it as a string`);
                return {
                    stringValue: decimal
                };
            }
            return {
                intValue: decimal
            };
        }
        return {
            doubleValue: value
        };
    }
    if ('string' == typeof value) return {
        stringValue: sanitizeString(value)
    };
    if ('function' == typeof value) return {
        stringValue: FUNCTION_VALUE
    };
    if ('symbol' == typeof value) return {
        stringValue: String(value)
    };
    if ('object' == typeof value && null !== value) {
        if (state.ancestors.has(value)) return {
            stringValue: CIRCULAR_VALUE
        };
        if (depth >= MAX_JSON_SAFE_VALUE_DEPTH) return {
            stringValue: TRUNCATED_VALUE
        };
        if (value instanceof Date) {
            const time = value.getTime();
            const iso = Number.isFinite(time) ? value.toISOString() : String(value);
            return {
                stringValue: 'string' == typeof iso ? sanitizeString(iso) : String(iso)
            };
        }
        state.ancestors.add(value);
        try {
            try {
                const toJSON = value.toJSON;
                if ('function' == typeof toJSON) return encodeAnyValue(toJSON.call(value), logger, state, depth + 1);
            } catch  {}
            if (isArray(value)) return {
                arrayValue: {
                    values: encodeArrayValues(value, logger, state, depth + 1)
                }
            };
            return {
                kvlistValue: {
                    values: encodeKeyValueList(value, logger, state, depth + 1)
                }
            };
        } finally{
            state.ancestors.delete(value);
        }
    }
    return {
        stringValue: sanitizeString(String(value))
    };
}
function encodeArrayValues(values, logger, state, depth) {
    const result = [];
    const itemCount = Math.min(values.length, MAX_JSON_SAFE_VALUE_ITEMS);
    let index = 0;
    for(; index < itemCount && state.remainingNodes > 0; index++)try {
        const element = index in values ? values[index] : void 0;
        if (isNullish(element)) continue;
        result.push(encodeAnyValue(element, logger, state, depth));
    } catch  {
        result.push({
            stringValue: UNSERIALIZABLE_VALUE
        });
    }
    if (values.length > index) result.push({
        stringValue: TRUNCATED_VALUE
    });
    return result;
}
function encodeKeyValueList(attrs, logger, state, depth) {
    const result = [];
    for(const key in attrs)if (propertyIsEnumerable.call(attrs, key)) {
        if (result.length >= MAX_JSON_SAFE_VALUE_ITEMS || state.remainingNodes <= 0) {
            logger?.debug('Attributes truncated: the value exceeds the OTLP encoder budget');
            break;
        }
        try {
            const value = attrs[key];
            if (isNull(value) || isUndefined(value)) continue;
            result.push({
                key: sanitizeString(key),
                value: encodeAnyValue(value, logger, state, depth)
            });
        } catch  {
            result.push({
                key: sanitizeString(key),
                value: {
                    stringValue: UNSERIALIZABLE_VALUE
                }
            });
        }
    }
    return result;
}
function timestampToUnixNano() {
    return String(Date.now()) + '000000';
}
function encodeBody(body) {
    try {
        return sanitizeString(String(body));
    } catch  {
        return UNSERIALIZABLE_VALUE;
    }
}
function buildOtlpLogRecord(options, sdkContext, logger) {
    const level = options.level || 'info';
    const { text: severityText, number: severityNumber } = OTLP_SEVERITY_MAP[level] || DEFAULT_OTLP_SEVERITY;
    const now = timestampToUnixNano();
    const autoAttributes = {};
    if (sdkContext.distinctId) autoAttributes.posthogDistinctId = sdkContext.distinctId;
    if (sdkContext.sessionId) autoAttributes.sessionId = sdkContext.sessionId;
    if (sdkContext.windowId) autoAttributes['window.id'] = sdkContext.windowId;
    if (!isNullish(sdkContext.sessionStartTimestamp)) autoAttributes.sessionStartTimestamp = String(sdkContext.sessionStartTimestamp);
    if (!isNullish(sdkContext.lastActivityTimestamp)) autoAttributes.lastActivityTimestamp = String(sdkContext.lastActivityTimestamp);
    if (sdkContext.currentUrl) autoAttributes['url.full'] = sdkContext.currentUrl;
    if (sdkContext.screenName) autoAttributes['screen.name'] = sdkContext.screenName;
    if (sdkContext.appState) autoAttributes['app.state'] = sdkContext.appState;
    if (sdkContext.activeFeatureFlags && sdkContext.activeFeatureFlags.length > 0) autoAttributes.feature_flags = sdkContext.activeFeatureFlags;
    const mergedAttributes = {
        ...autoAttributes
    };
    const userAttributes = options.attributes;
    if (userAttributes) {
        let keys = [];
        try {
            keys = Object.keys(userAttributes);
        } catch  {
            keys = [];
        }
        for (const key of keys){
            let value;
            try {
                value = userAttributes[key];
            } catch  {
                value = UNSERIALIZABLE_VALUE;
            }
            Object.defineProperty(mergedAttributes, key, {
                value,
                enumerable: true,
                writable: true,
                configurable: true
            });
        }
    }
    const record = {
        timeUnixNano: now,
        observedTimeUnixNano: now,
        severityNumber,
        severityText,
        body: {
            stringValue: encodeBody(options.body)
        },
        attributes: toOtlpKeyValueList(mergedAttributes, logger)
    };
    if (options.trace_id) record.traceId = options.trace_id;
    if (options.span_id) record.spanId = options.span_id;
    if (!isUndefined(options.trace_flags)) record.flags = options.trace_flags;
    return record;
}
function buildResourceAttributes(config, sdkName, sdkVersion) {
    return {
        ...config.resourceAttributes,
        'service.name': config.serviceName || 'unknown_service',
        ...config.environment && {
            'deployment.environment': config.environment
        },
        ...config.serviceVersion && {
            'service.version': config.serviceVersion
        },
        'telemetry.sdk.name': sdkName,
        'telemetry.sdk.version': sdkVersion
    };
}
function buildOtlpLogsPayload(logRecords, resourceAttributes, scopeName, scopeVersion) {
    return {
        resourceLogs: [
            {
                resource: {
                    attributes: toOtlpKeyValueList(resourceAttributes)
                },
                scopeLogs: [
                    {
                        scope: {
                            name: scopeName,
                            version: scopeVersion
                        },
                        logRecords
                    }
                ]
            }
        ]
    };
}
export { buildOtlpLogRecord, buildOtlpLogsPayload, buildResourceAttributes, getOtlpSeverityNumber, getOtlpSeverityText, toOtlpAnyValue, toOtlpKeyValueList };
